import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useToastr } from '@farmshare/ui-components';

export interface UploadImageModalProps {
  open: boolean;
  onClose: () => void;
  specificationIndex: number;
  currentImageUrl?: string | undefined;
  onImageSelected: (file: File, index: number) => void;
}

export function UploadImageModal({
  open,
  onClose,
  specificationIndex,
  currentImageUrl = undefined,
  onImageSelected,
}: UploadImageModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { push } = useToastr();

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  // Clean up object URLs on unmount
  useEffect(
    () => () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl],
  );

  // Set preview from current image URL when modal opens
  useEffect(() => {
    if (open && currentImageUrl && !selectedFile) {
      setPreviewUrl(currentImageUrl);
    } else if (open && !currentImageUrl && !selectedFile) {
      setPreviewUrl(null);
    }
  }, [open, currentImageUrl, selectedFile]);

  const handleClose = () => {
    // Clean up preview URL if it's an object URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      push({
        title: 'Invalid File Type',
        body: 'Please select a JPG, PNG, or GIF file.',
        severity: 'error',
        delay: 5000,
      });
      return;
    }
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      push({
        title: 'File Too Large',
        body: 'File size must be 5MB or less.',
        severity: 'error',
        delay: 5000,
      });
      return;
    }
    setSelectedFile(file);
    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    try {
      onImageSelected(selectedFile, specificationIndex);
      handleClose();
    } catch (error) {
      push({
        title: 'Upload Error',
        body:
          error instanceof Error
            ? error.message
            : 'Failed to process image selection.',
        severity: 'error',
        delay: 5000,
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload an image</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <Box
            sx={{
              border: 2,
              borderColor: 'divider',
              borderStyle: 'dashed',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={handleUploadClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Stack spacing={2} alignItems="center">
              {previewUrl ? (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    maxHeight: 300,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </Box>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUploadClick();
                    }}
                  >
                    Upload an image
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    JPG, PNG, or GIF, 5MB or less
                  </Typography>
                </>
              )}
              {selectedFile && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              {previewUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUploadClick();
                  }}
                >
                  {currentImageUrl ? 'Change Image' : 'Select Different Image'}
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!selectedFile}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
